import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DonutChartComponent, DonutChartSegment } from '@softapps/core/chart';
import {
  CardComponent,
  CardDescriptionComponent,
  CardHeaderComponent,
  CardLeadingComponent,
  CardTitleComponent,
  CardTrailingComponent,
  IconComponent,
  LoadingSpinnerComponent,
  TranslatePipe,
} from '@softline/ui-core';
import { AbteilungUebersicht } from '../../types/abteilung';

interface PresenceSummary {
  /** False until the department's own status request has answered. */
  loaded: boolean;
  total: number;
  present: number;
  absentRed: number;
  absentYellow: number;
}

@Component({
  selector: 'soft-abteilung-card',
  imports: [
    CardComponent,
    CardDescriptionComponent,
    CardHeaderComponent,
    CardLeadingComponent,
    CardTitleComponent,
    CardTrailingComponent,
    DonutChartComponent,
    IconComponent,
    LoadingSpinnerComponent,
    NgTemplateOutlet,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './abteilung-card.component.html',
  styleUrl: './abteilung-card.component.css',
})
export class AbteilungCardComponent {
  readonly abteilung = input.required<AbteilungUebersicht>();
  readonly selected = input(false);
  readonly mode = input<'selection' | 'display'>('display');
  readonly grid = input(false);
  readonly tapped = output<void>();

  protected readonly isSelected = computed(() => this.mode() === 'selection' && this.selected());

  /** Pseudo persons named "Abteilung" / "Vertrieb" are bookkeeping rows and leave the counts. */
  protected readonly summary = computed<PresenceSummary>(() => {
    const abteilung = this.abteilung();
    const persons = abteilung.personen ?? [];
    const excluded = persons.filter((p) => {
      const name = p.vorname?.toLowerCase() ?? '';
      return name.includes('abteilung') || name.includes('vertrieb');
    }).length;
    const present = abteilung.status?.anwesend ?? 0;
    const absentRed = (abteilung.status?.abwesendrot ?? 0) - excluded;
    const absentYellow = abteilung.status?.abwesendgelb ?? 0;
    return {
      loaded: !!abteilung.status,
      total: present + absentRed + absentYellow,
      present,
      absentRed,
      absentYellow,
    };
  });

  protected readonly segments = computed<DonutChartSegment[]>(() => {
    const summary = this.summary();
    return [
      {
        label: '#PMS_STATUSABFRAGE.ABTEILUNGEN.PERSONEN_ANWESEND',
        value: summary.present,
        color: 'var(--colorSuccess500)',
      },
      {
        label: '#PMS_STATUSABFRAGE.ABTEILUNGEN.PERSONEN_ABWESEND_ROT',
        value: summary.absentRed,
        color: 'var(--colorDanger500)',
      },
      {
        label: '#PMS_STATUSABFRAGE.ABTEILUNGEN.PERSONEN_ABWESEND_GELB',
        value: summary.absentYellow,
        color: 'var(--colorWarning500)',
      },
    ];
  });
}
